import React, { useEffect, useRef, useState } from "react";
import Sidebar from "./components/Sidebar";
import RoutesDashboard from "./pages/Routes/RoutesDashboard";
import LeadDetails from "./pages/Routes/LeadDetails";
import Inspection from "./pages/Routes/Inspection";
import QuoteBuilder from "./pages/Sales/QuoteBuilder";
import Communications from "./pages/Communications/Communications";
import Calls from "./pages/Calls/Calls";
import Dashboard from "./pages/Dashboard/Dashboard";
import Rebook from "./pages/Rebook/Rebook";
import NotBuilt from "./pages/Rebook/NotBuilt";
import SyncStatus from "./components/SyncStatus";
import CallBar from "./components/CallBar";
import SearchBar from "./components/SearchBar";
import CustomerAccount from "./pages/Customers/CustomerAccount";
import JobProgress from "./pages/Jobs/JobProgress";
import Settings from "./pages/Settings/Settings";
import { NewTextBanner } from "./components/TextAlerts";
import { refreshAlertState, registerServiceWorker, unlockChime } from "./services/textAlerts";
import { queueChange } from "./services/sync";
import { getLead, refreshLeads } from "./services/leads";
import { refreshCatalog } from "./services/catalog";
import { refreshDrafts } from "./services/drafts";
import { refreshCalls } from "./services/callsBoard";
import { openSyncPanel } from "./components/SyncStatus";
import { useUnreadTexts } from "./services/communications";
import "./App.css";

/**
 * CIE Admin Dashboard
 *
 * Layout:
 * - Top: header with customer search (hidden while an inspection / report is open) + the Zoho sync indicator
 * - Left: Sidebar (slides in on a phone)
 * - Right: the current page
 */

const NOT_BUILT = {
  tasks: ["✓", "Tasks"],
  leads: ["📊", "Lead Center"],
  analytics: ["📈", "Analytics"],
  resources: ["📚", "Resources"],
  pay: ["💳", "Pay Tracker"],
};

function App() {
  const [currentPage, setCurrentPage] = useState("route");
  const [selectedInspection, setSelectedInspection] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Communications opened for one customer, and where Back goes from there
  const [commsLeadId, setCommsLeadId] = useState(null);
  const [commsReturn, setCommsReturn] = useState(null); // { page, followUpId? }
  const [commsCustomer, setCommsCustomer] = useState(null); // a customer's thread opened from their account
  // the customer account (360° view) and the page Back returns to
  const [customerId, setCustomerId] = useState(null);
  const [customerReturn, setCustomerReturn] = useState("route");
  // the follow-up open on the Calls page (so Back from Communications lands on it again)
  const [callsOpenId, setCallsOpenId] = useState(null);
  const [callsTab, setCallsTab] = useState("followUp");
  const [inspectionTab, setInspectionTab] = useState("new");
  const unreadTexts = useUnreadTexts(); // shown on the ☰ button too, since the menu is hidden on a phone
  const headerRef = useRef(null);

  // the phone menu opens right under the header, whose height changes with the search row and the notch
  useEffect(() => {
    const el = headerRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const set = () => document.documentElement.style.setProperty("--header-h", `${el.offsetHeight}px`);
    set();
    const ro = new ResizeObserver(set);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // appointments and the service catalog come from Zoho: load after sign-in, and again whenever the app comes back to the front
  useEffect(() => {
    const refresh = () => {
      refreshLeads();
      refreshCatalog();
      refreshDrafts(); // everyone's drafts and sent inspections
      refreshCalls(); // the Calls board (Zoho)
    };
    refresh();
    const onShow = () => document.visibilityState === "visible" && refresh();
    document.addEventListener("visibilitychange", onShow);
    return () => document.removeEventListener("visibilitychange", onShow);
  }, []);

  const handleSelectInspection = (inspection) => {
    setSelectedInspection(inspection);
    setCurrentPage("details");
  };

  const handleBackToDashboard = () => {
    setCurrentPage("route");
    setSelectedInspection(null);
  };

  const handleBackToDetails = () => {
    setCurrentPage("details");
  };

  const handlePageChange = (page) => {
    if (page === "communications") {
      setCommsLeadId(null); // from the sidebar: the full inbox
      setCommsCustomer(null);
    }
    // from the sidebar these start fresh — never attached to the last lead you looked at
    if (page === "inspection" || page === "quotes") setSelectedInspection(null);
    if (page === "calls") {
      setCallsOpenId(null);
      setCallsTab("followUp");
    }
    if (page === "inspection") setInspectionTab("new");
    setCurrentPage(page);
    setSidebarOpen(false);
  };

  // from the dashboard: open a page on the right tab
  const navigateFromDashboard = (page, opts = {}) => {
    if (page === "sync") return openSyncPanel();
    handlePageChange(page);
    if (page === "calls" && opts.tab) setCallsTab(opts.tab);
    if (page === "inspection" && opts.tab) setInspectionTab(opts.tab);
    return undefined;
  };

  // a Calls customer as the lead the Route / Inspection pages expect (the route's own copy when it has one)
  const leadFromCall = (item) =>
    getLead(item.inspectionId) || {
      id: item.inspectionId,
      contactName: item.name,
      servicePhone: item.phone,
      serviceAddress: item.address,
      email: item.email,
      stage: item.section === "needsQuote" ? "Report Pending" : item.zohoStage === "Cancelled" ? "Cancelled" : "Rebook",
      appointmentStart: item.appointmentStart,
      agentName: item.agent,
      customerNotes: item.concern ? `Concern: ${item.concern}` : "",
      statusHistory: [],
    };

  const openCommunication = (contactId, returnTo, customer = null) => {
    setCommsLeadId(contactId);
    setCommsCustomer(customer);
    setCommsReturn(returnTo);
    setCurrentPage("communications");
  };

  // a customer's thread from a text alert (in-app banner or a phone notification)
  const openThread = (customer) => {
    if (!customer || !customer.key) return;
    openCommunication(null, { page: currentPage === "communications" ? "route" : currentPage }, { name: "", phone: "", email: "", ...customer });
  };

  // text alerts: the notification helper, sound unlocked on the first tap, and "open this thread" from a
  // tapped notification (?thread=<key> when the app was closed, a message when it was already open)
  useEffect(() => {
    registerServiceWorker();
    refreshAlertState();
    const unlock = () => unlockChime();
    window.addEventListener("pointerdown", unlock, { once: true });
    const fromUrl = (url) => {
      const key = new URL(url, window.location.origin).searchParams.get("thread");
      if (key) openThread({ key });
    };
    fromUrl(window.location.href);
    if (window.location.search.includes("thread=")) window.history.replaceState(null, "", "/");
    const onMessage = (e) => e.data && e.data.type === "cie-open" && fromUrl(e.data.url);
    if (navigator.serviceWorker) navigator.serviceWorker.addEventListener("message", onMessage);
    return () => {
      window.removeEventListener("pointerdown", unlock);
      if (navigator.serviceWorker) navigator.serviceWorker.removeEventListener("message", onMessage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // search, a name in Communications, or 360° View → the customer's account
  const openCustomer = (id) => {
    if (!id) return;
    if (currentPage !== "customer") setCustomerReturn(currentPage);
    setCustomerId(id);
    setCurrentPage("customer");
    setSidebarOpen(false);
  };

  const backFromCommunication = () => {
    if (commsReturn && commsReturn.page === "calls") setCallsOpenId(commsReturn.followUpId || null);
    setCurrentPage(commsReturn ? commsReturn.page : "details");
  };

  return (
    <div className="app">
      {/* a call made from the app — visible on every page until it ends */}
      <CallBar />
      {/* a customer just texted / emailed — on every page, tap to open their thread */}
      <NewTextBanner onOpen={openThread} />
      {/* HEADER */}
      <header className="app-header" ref={headerRef}>
        <button className="menu-btn" onClick={() => setSidebarOpen(!sidebarOpen)} aria-label="Toggle menu">
          ☰
          {unreadTexts > 0 && <span className="menu-badge" aria-label={`${unreadTexts} unread texts`} />}
        </button>
        {/* the logo lives here (not in the menu) so it's on every page, phone included */}
        <div className="header-logo">
          <img src="/cie-logo-white-tight.png" alt="Canadian Insulation Experts" className="header-logo-img" />
        </div>
        <div className="header-spacer"></div>
        {/* is what staff did in the app actually in Zoho? */}
        <SyncStatus />
        {/* customer search: the header's second row, on every page except while doing an inspection / report */}
        {currentPage !== "inspection" && (
          <div className="header-search">
            <SearchBar onOpenCustomer={(c) => openCustomer(c.id)} />
          </div>
        )}
      </header>

      {/* LAYOUT CONTAINER - Sidebar + Main Content */}
      <div className="layout-container">
        <div className={`sidebar-wrapper ${sidebarOpen ? "open" : ""}`}>
          <Sidebar currentPage={currentPage} onPageChange={handlePageChange} />
        </div>

        {sidebarOpen && <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)} />}

        <div className="main-content">
          {currentPage === "dashboard" && <Dashboard onNavigate={navigateFromDashboard} />}

          {currentPage === "route" && <RoutesDashboard onSelectInspection={handleSelectInspection} />}

          {currentPage === "details" && selectedInspection && (
            <LeadDetails
              inspection={selectedInspection}
              onBack={handleBackToDashboard}
              onStartInspection={() => setCurrentPage("inspection")}
              onOpenCommunication={() => openCommunication(selectedInspection.id, { page: "details" })}
              onOpenAccount={() => openCustomer(selectedInspection.id)}
            />
          )}

          {currentPage === "inspection" && (
            <Inspection
              key={selectedInspection ? selectedInspection.id : `new-${inspectionTab}`}
              inspection={selectedInspection}
              initialTab={selectedInspection ? "new" : inspectionTab}
              onBack={selectedInspection ? handleBackToDetails : () => setCurrentPage("route")}
              onFinished={handleBackToDashboard}
              onOpenCommunication={(leadId) => openCommunication(leadId, { page: "inspection" })}
            />
          )}

          {currentPage === "communications" && (
            <Communications
              key={(commsCustomer && commsCustomer.key) || commsLeadId || "inbox"}
              leadId={commsLeadId}
              customer={commsCustomer}
              onBackToLead={commsLeadId || commsCustomer ? backFromCommunication : undefined}
              onOpenCustomer={openCustomer}
            />
          )}

          {currentPage === "calls" && (
            <Calls
              key={callsTab}
              initialOpenId={callsOpenId}
              initialTab={callsTab}
              onOpenCommunication={(contactId, followUpId) => openCommunication(contactId, { page: "calls", followUpId })}
              onOpenInspection={(item) => {
                setSelectedInspection(leadFromCall(item));
                setCurrentPage("inspection");
              }}
              onOpenRebook={(item) => handleSelectInspection(leadFromCall(item))}
            />
          )}

          {currentPage === "customer" && customerId && (
            <CustomerAccount
              key={customerId}
              id={customerId}
              onBack={() => setCurrentPage(customerReturn === "customer" ? "route" : customerReturn)}
              onOpenCommunication={(customer) => openCommunication(null, { page: "customer" }, customer)}
            />
          )}

          {/* Job Progress: the same jobs split over two pages (key = a fresh page from the menu) */}
          {(currentPage === "jobsProgress" || currentPage === "jobsCompleted") && (
            <JobProgress
              key={currentPage}
              page={currentPage === "jobsCompleted" ? "completed" : "progress"}
              onOpenCustomer={openCustomer}
            />
          )}

          {currentPage === "rebook" && <Rebook onSelectLead={handleSelectInspection} />}

          {currentPage === "quotes" && (
            // quote-only: no lead, no report — just the quotes
            <QuoteBuilder
              key="quote-only"
              inspection={null}
              onBack={() => setCurrentPage("route")}
              onContinue={(sendData) =>
                queueChange({
                  type: "quote.sent",
                  label: `${sendData.packages.length} quote${sendData.packages.length === 1 ? "" : "s"} sent to ${[...sendData.emails, ...sendData.phones][0] || "customer"}`,
                  payload: { emails: sendData.emails, phones: sendData.phones, packages: sendData.packages.map((p) => p.name) },
                })
              }
            />
          )}

          {currentPage === "settings" && <Settings />}
          {NOT_BUILT[currentPage] && <NotBuilt icon={NOT_BUILT[currentPage][0]} title={NOT_BUILT[currentPage][1]} />}
        </div>
      </div>
    </div>
  );
}

export default App;
